// Date helpers — always interpret patient.intervention_date as a Europe/Rome
// calendar date. Avoids UTC-vs-local mismatches when `new Date("YYYY-MM-DD")`
// would otherwise parse as UTC midnight.

const ROME_TZ = "Europe/Rome";

/**
 * Parse an intervention_date string into a {y,m,d} calendar date in Rome.
 * Accepts:
 *   - "YYYY-MM-DD"
 *   - ISO 8601 with time (e.g. "2026-06-23T00:00:00Z", "2026-06-23T22:00:00+02:00")
 *   - "DD/MM/YYYY" (Italian)
 *   - "DD-MM-YYYY"
 * Returns null on invalid input.
 */
function parseInterventionDate(raw) {
  if (!raw) return null;
  const s = String(raw).trim();
  if (!s) return null;
  // 1) Plain YYYY-MM-DD
  let m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (m) return { y: +m[1], m: +m[2], d: +m[3] };
  // 2) ISO 8601 with time -> convert to Rome calendar date
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) {
    const dt = new Date(s);
    if (!isNaN(dt.getTime())) {
      // 'sv-SE' locale formats as YYYY-MM-DD, with timezone option
      const romeStr = dt.toLocaleDateString("sv-SE", { timeZone: ROME_TZ });
      const m2 = /^(\d{4})-(\d{2})-(\d{2})$/.exec(romeStr);
      if (m2) return { y: +m2[1], m: +m2[2], d: +m2[3] };
    }
  }
  // 3) Italian formats DD/MM/YYYY or DD-MM-YYYY
  m = /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/.exec(s);
  if (m) return { y: +m[3], m: +m[2], d: +m[1] };
  return null;
}

function todayInRome() {
  const romeStr = new Date().toLocaleDateString("sv-SE", { timeZone: ROME_TZ });
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(romeStr);
  if (!m) return null;
  return { y: +m[1], m: +m[2], d: +m[3] };
}

function _toDays({ y, m, d }) {
  // Use Date.UTC to compute days difference unaffected by DST / local TZ.
  return Math.floor(Date.UTC(y, m - 1, d) / 86400000);
}

/**
 * Days elapsed since the intervention, in Europe/Rome calendar days.
 *   surgery today           -> 0
 *   surgery yesterday       -> 1
 *   surgery N days ago      -> N
 *   future / invalid input  -> 0
 */
export function computePostOpDay(interventionDate) {
  const surg = parseInterventionDate(interventionDate);
  if (!surg) return 0;
  const today = todayInRome();
  if (!today) return 0;
  const diff = _toDays(today) - _toDays(surg);
  return Math.max(0, diff);
}

export { parseInterventionDate, todayInRome };
